//import _debounce from 'lodash/debounce';
import React, { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FormSkeleton } from '../../../../components/UI/Skeleton/Skeleton';
import EmptyData from '../../../../components/UI/EmptyData';
import './kpiModal.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';

import {
  useGetPerformanceQuery,
  useAddPerformanceMutation,
  useEditPerformanceMutation,
  useGetKpiPerformanceByFilterQuery
} from '../../../../redux/apis/PerformanceApi';
import { selectedKpi } from '../../../../redux/Slice/kpiSlice';
import { useIsLoggedInQuery } from '../../../../redux/apis/authApi';

import { GLOBAL } from '../../../../utils/constant/global';
import { notification } from '../../../../utils/notification';
import { TOAST_TYPE } from '../../../../utils/constant/errorMessage';
import { BT_BUTTON_LABELS } from '../../../../utils/constant/buttons';
import { INPUT_LABEL } from '../../../../utils/constant/placeHolders';
import { performanceFormSchema } from '../../../../utils/schemas/performance';
import { skipRow, handleApiError, formattedAverage } from '../../../../utils/handler';

const { DESC, ASC } = GLOBAL;
const { BTN_SAVE, BTN_CANCEL } = BT_BUTTON_LABELS;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;
const { LB_NOTES, LB_POINTS, LB_AVG } = INPUT_LABEL;

const KPIFormModel = ({ state, setState, onHandleSubmit }) => {
  const dispatch = useDispatch();

  // eslint-disable-next-line no-unused-vars
  const { refetch: performanceFetch } = useGetPerformanceQuery({
    search: state?.search || '',
    skip: skipRow(state?.currentPage, state?.rowsPerPage),
    limit: state?.rowsPerPage,
    sort: state?.sortField,
    sortType: state.sortDirection ? DESC : ASC
  });

  const selectedPerformance = useSelector((state) => state.performance.selectedPerformance);

  const {
    data: selectedKpiData,
    isLoading: kpiPerformanceLoading,
    refetch: kpiPerformanceFetch
  } = useGetKpiPerformanceByFilterQuery({
    selectedYear: selectedPerformance?.selectedYear,
    selectedMonth: selectedPerformance?.selectedMonth,
    userId: selectedPerformance?._id
  });

  const { data: isLoggedInData } = useIsLoggedInQuery();

  const [addPerformance] = useAddPerformanceMutation();
  const [editPerformance] = useEditPerformanceMutation();

  useEffect(() => {
    if (selectedPerformance?._id) kpiPerformanceFetch();
  }, [selectedPerformance]);

  useEffect(() => {
    handleSetEditFormData();
  }, [selectedKpiData]);

  const {
    register,
    reset,
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(performanceFormSchema),
    values: {
      kpis:
        selectedKpiData?.data?.kpis?.length > 0
          ? selectedKpiData?.data.kpis.map((kpi) => ({
              name: kpi.value,
              value: kpi.name,
              kpiId: kpi.kpiId,
              notes: kpi.notes
            }))
          : [],
      average: 0
    }
  });

  const { fields } = useFieldArray({
    control,
    name: 'kpis'
  });

  // Watch kpis array for changes
  const kpisWatch = watch('kpis');

  useEffect(() => {
    handleAverageCalculation();
  }, [JSON.stringify(kpisWatch)]);

  const handleAverageCalculation = () => {
    // Ensure kpisWatch is an array
    if (!Array.isArray(kpisWatch) || kpisWatch.length === 0) {
      setValue('average', 0);
      return;
    }

    const newKpiWatch = kpisWatch.filter((k) => k.name !== '');

    // Calculate the average whenever kpisWatch changes
    const total = newKpiWatch.reduce((acc, kpi) => acc + (Number(kpi.name) || 0), 0);
    const average = newKpiWatch.length > 0 ? total / newKpiWatch.length : 0;

    // Only set value if it has changed to avoid unnecessary renders
    if (average !== getValues('average')) {
      setValue('average', formattedAverage(average));
    }
  };

  const getErrorMessage = (field, errors) => {
    return errors[field] ? errors[field].message : '';
  };

  const handleSetEditFormData = () => {
    if (selectedKpiData?.data) {
      setValue('notes', selectedKpiData?.data?.notes || '');
    }
  };

  const handleResetState = () => {
    reset();
    setState({
      ...state,
      isModal: { model: false, submit: false },
      loading: false,
      modalHeader: { title: '', isEdit: false, editId: '' }
    });
  };

  const handleAddKpi = async (data) => {
    try {
      // Update state to indicate loading state
      setState((prevState) => ({
        ...prevState,
        loading: true
      }));

      if (data) data = { ...data, givenBy: isLoggedInData?.data?._id };

      // Perform the API call to add the KPI data (assuming addLeaveType is your API function)
      const { data: resData, error } = await addPerformance(data);

      if (resData?.success) {
        onHandleSubmit();
        notification(resData.statusMessage, TOAST_SUCCESS);
        handleResetState();
      } else if (error) {
        handleApiError(error);
        handleResetState();
      }
    } catch (error) {
      notification(error?.data?.data?.message, TOAST_ERROR);
      handleResetState();
    }
  };

  const isEditKPI = async (data, id) => {
    try {
      const editData = {
        data,
        id
      };

      setState({ ...state, loading: true });

      // Call editKpi and handle the response
      const { data: resData, error } = await editPerformance(editData);

      if (resData?.success) {
        onHandleSubmit();
        notification(resData.statusMessage, TOAST_SUCCESS);
        handleResetState();
      } else if (error) {
        handleApiError(error);
        handleResetState();
      }
    } catch (error) {
      notification(error?.message, TOAST_ERROR);
      handleResetState();
    }
  };

  const onSubmit = (data) => {
    // Update state to show modal and indicate submission in progress
    setState((prevState) => ({
      ...prevState,
      isModal: { model: true, submit: true }
    }));

    data = {
      ...data,
      userId: selectedPerformance._id,
      year: selectedPerformance?.selectedYear,
      month: selectedPerformance?.selectedMonth
    };

    if (data?.kpis?.length > 0) {
      data.kpis = data.kpis.map((item) => ({
        name: item.value,
        value: item.name,
        kpiId: item.kpiId,
        notes: item.notes
      }));
    }

    // Determine whether to edit existing data or add new data
    if (selectedKpiData?.data?._id) {
      isEditKPI(data, selectedKpiData.data._id);
    } else {
      handleAddKpi(data);
    }
  };

  const handleDialogCancel = () => {
    setState({ ...state, isModal: { model: false, submit: false } });
    dispatch(selectedKpi({}));
  };

  const kpiLoaderComponent = () => {
    if (!selectedKpiData && kpiPerformanceLoading) {
      return (
        <div className="loading-card w-100">
          <FormSkeleton rows={5} twoCol />
        </div>
      );
    } else if (selectedKpiData && (!selectedKpiData.data || selectedKpiData.data.length === 0)) {
      return (
        <EmptyData
          title={GLOBAL.NO_KPI_FOUND}
          subtitle="There are no KPIs configured for this period yet."
        />
      );
    } else if (!selectedKpiData?.data?.kpis || selectedKpiData.data.kpis.length === 0) {
      return (
        <EmptyData
          title={GLOBAL.NO_KPI_FOUND}
          subtitle="There are no KPIs configured for this period yet."
        />
      );
    }
    return null; // Default return value if none of the conditions are met
  };

  const monthName = selectedPerformance?.selectedMonth || '';
  const monthLabel = monthName ? monthName.charAt(0).toUpperCase() + monthName.slice(1) : '';
  const periodLabel = [monthLabel, selectedPerformance?.selectedYear].filter(Boolean).join(' ');
  const subtitle = state?.modalHeader?.isEdit
    ? periodLabel
      ? `Update the scores for ${periodLabel}`
      : 'Update the scores for this period'
    : periodLabel
      ? `Enter the scores for ${periodLabel}`
      : 'Enter the scores for this period';

  const closeModal = () => {
    reset();
    setState({ ...state, isModal: { model: false, submit: false } });
  };

  return (
    <Modal
      centered
      className="perf-modal"
      dialogClassName="perf-modal__dialog"
      contentClassName="perf-modal__content"
      show={state?.isModal?.model}
      onHide={closeModal}
    >
      <Modal.Header className="perf-modal__header">
        <div className="perf-modal__header-content">
          <span className="perf-modal__header-icon" aria-hidden="true">
            <i className="icofont-medal" />
          </span>
          <div className="perf-modal__header-copy">
            <Modal.Title className="fw-bold perf-modal__title">
              {state?.modalHeader?.title}
            </Modal.Title>
            <p className="perf-modal__subtitle">{subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close perf-modal__close"
          aria-label="Close"
          onClick={closeModal}
        />
      </Modal.Header>
      {selectedKpiData?.data?.kpis?.length > 0 ? (
        <form className="perf-modal__form" onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="kpi-modal-body perf-modal__body">
            {fields.map((field, index) => (
              <div key={field.id} className="perf-modal__kpi">
                <div className="perf-modal__kpi-title">
                  <span>{selectedKpiData?.data?.kpis?.[index]?.name || 'KPI Name'}</span>
                  <i
                    className="bi bi-info-circle-fill perf-modal__info"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={selectedKpiData?.data?.kpis?.[index]?.description || ''}
                  />
                </div>
                <div className="perf-modal__kpi-fields">
                  <div>
                    <label htmlFor={`kpis.${index}.name`} className="perf-modal__label">
                      {LB_POINTS}
                    </label>
                    <input
                      type="text"
                      {...register(`kpis.${index}.name`, { required: 'Name is required' })}
                      className="form-control perf-modal__input"
                      placeholder={LB_POINTS}
                      id={`kpis.${index}.name`}
                      onChange={(data) => {
                        setValue(`kpis.${index}.name`, data.target.value);
                      }}
                    />
                    <small className="text-danger perf-modal__error">
                      {errors?.['kpis']?.[index]?.name?.message || ''}
                    </small>
                  </div>
                  <div>
                    <label htmlFor={`kpis.${index}.notes`} className="perf-modal__label">
                      Notes
                    </label>
                    <input
                      type="text"
                      {...register(`kpis.${index}.notes`)}
                      className="form-control perf-modal__input"
                      placeholder={LB_NOTES}
                      id={`kpis.${index}.notes`}
                      onChange={(data) => {
                        setValue(`kpis.${index}.notes`, data.target.value);
                      }}
                    />
                    <small className="text-danger perf-modal__error">
                      {errors?.['kpis']?.[index]?.notes?.message || ''}
                    </small>
                  </div>
                </div>
              </div>
            ))}
            <div className="perf-modal__average">
              <label htmlFor="performance-average" className="perf-modal__label">
                {LB_AVG}
              </label>
              <input
                id="performance-average"
                type="text"
                {...register('average')}
                className="form-control perf-modal__input perf-modal__input--average"
                placeholder={LB_AVG}
                disabled={true}
              />
              <small className="text-danger perf-modal__error">
                {getErrorMessage('average', errors)}
              </small>
            </div>
            {/* <div className="mb-3">
              <label htmlFor="exampleFormControlInput1111" className="form-label">
                {LB_NOTES}
              </label>
              <textarea
                type="text"
                {...register('notes')}
                placeholder={LB_NOTES}
                className="form-control"
              />
              <small className="text-danger">{getErrorMessage('notes', errors)}</small>
            </div> */}
          </Modal.Body>
          <Modal.Footer className="perf-modal__footer">
            <button type="button" className="btn perf-modal__cancel" onClick={handleDialogCancel}>
              {BTN_CANCEL}
            </button>
            <button
              type="submit"
              disabled={state?.loading || false}
              className="btn perf-modal__submit"
            >
              {state?.loading ? (
                <div className="spinner-border spinner-border-sm" role="status"></div>
              ) : (
                <>
                  <i className="icofont-check-circled" aria-hidden="true" />
                  {BTN_SAVE}
                </>
              )}
            </button>
          </Modal.Footer>
        </form>
      ) : (
        <Modal.Body className="kpi-modal-body perf-modal__body">{kpiLoaderComponent()}</Modal.Body>
      )}
    </Modal>
  );
};

export default KPIFormModel;
